(() => {
  const init = (root = document) => {
  const form = root.querySelector('#KeeneSendForm');
  if (!form || form.dataset.sendInitialized === 'true') return;
  form.dataset.sendInitialized = 'true';

  const locations = [...form.querySelectorAll('[data-keene-send-location]')];
  const geoOverlay = form.querySelector('[data-send-geo-overlay]');
  const fieldsets = [...form.querySelectorAll('fieldset')];
  const mapUrl = (latitude, longitude) =>
    `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${latitude},${longitude}`)}`;
  const setGeoLoading = (active) => {
    geoOverlay.hidden = !active;
    form.setAttribute('aria-busy', String(active));
    for (const fieldset of fieldsets) fieldset.inert = active;
  };

  for (const location of locations) {
    const locateButton = location.querySelector('[data-send-locate]');
    const manualButton = location.querySelector('[data-send-manual]');
    const method = location.querySelector('[data-send-method]');
    const mapLink = location.querySelector('[data-send-map-link]');
    const latitudeField = location.querySelector('[data-send-latitude]');
    const longitudeField = location.querySelector('[data-send-longitude]');
    const accuracy = location.querySelector('[data-send-accuracy]');
    const preview = location.querySelector('[data-send-preview]');
    const status = location.querySelector('[data-send-status]');
    const details = location.querySelector('[data-send-details]');
    const detailsLabel = location.querySelector('[data-send-details-label]');
    const detailsHelp = location.querySelector('[data-send-details-help]');
    let requestNumber = 0;
    let manualDraft = details.value;
    let pinDraft = '';

    locateButton.hidden = false;
    manualButton.hidden = false;
    const orDivider = location.querySelector('[data-send-location-or]');
    if (orDivider) orDivider.hidden = false;

    const setStatus = (message, kind = '') => {
      status.textContent = message;
      status.classList.toggle('is-error', kind === 'error');
      status.classList.toggle('is-success', kind === 'success');
    };

    setStatus('Enter the address below, or use your current location if you are at this point.');

    const showManual = (message = 'Enter the full address below.') => {
      requestNumber += 1;
      setGeoLoading(false);
      if (method.value === 'Current location') {
        pinDraft = details.value;
        details.value = manualDraft;
      }
      method.value = 'Manual address';
      mapLink.value = '';
      latitudeField.value = '';
      longitudeField.value = '';
      accuracy.value = '';
      preview.hidden = true;
      preview.removeAttribute('href');
      locateButton.classList.remove('is-selected');
      locateButton.setAttribute('aria-pressed', 'false');
      locateButton.disabled = false;
      manualButton.classList.add('is-selected');
      manualButton.setAttribute('aria-pressed', 'true');
      detailsLabel.innerHTML = 'Full address <em>Required</em>';
      details.placeholder = 'Street or area, building, and a nearby landmark';
      detailsHelp.textContent = 'Include enough detail for a rider to find the exact pickup or dropoff point.';
      setStatus(message);
    };

    manualButton.addEventListener('click', () => {
      showManual();
      details.focus();
    });

    locateButton.addEventListener('click', () => {
      if (!navigator.geolocation) {
        showManual('Your browser cannot share its location. Enter the address below.');
        return;
      }

      const currentRequest = ++requestNumber;
      locateButton.disabled = true;
      setStatus('Finding your current location…');
      setGeoLoading(true);
      try {
        navigator.geolocation.getCurrentPosition(
        ({ coords }) => {
          if (currentRequest !== requestNumber || !location.isConnected) return;
          locateButton.disabled = false;
          const { latitude, longitude, accuracy: metres } = coords;
          if (!Number.isFinite(latitude) || Math.abs(latitude) > 90 || !Number.isFinite(longitude) || Math.abs(longitude) > 180 || !Number.isFinite(metres) || metres < 0) {
            showManual('We could not read a valid location. Enter the address below.');
            return;
          }
          if (metres > 300) {
            showManual('This location is too imprecise for a rider. Enter the address below.');
            return;
          }

          const url = mapUrl(latitude, longitude);
          if (method.value === 'Manual address') {
            manualDraft = details.value;
            details.value = pinDraft;
          }
          method.value = 'Current location';
          mapLink.value = url;
          latitudeField.value = String(latitude);
          longitudeField.value = String(longitude);
          accuracy.value = `${Math.round(metres)} metres`;
          preview.href = url;
          preview.hidden = false;
          locateButton.classList.add('is-selected');
          locateButton.setAttribute('aria-pressed', 'true');
          manualButton.classList.remove('is-selected');
          manualButton.setAttribute('aria-pressed', 'false');
          detailsLabel.innerHTML = 'Nearby landmark or entrance <em>Required</em>';
          details.placeholder = 'For example, blue gate beside the pharmacy';
          detailsHelp.textContent = 'Your current location is a pin. Add a landmark so the rider finds the right entrance.';
          setStatus(`Location selected (accurate to about ${Math.round(metres)} metres). Check the pin before sending.`, 'success');
          setGeoLoading(false);
        },
        (error) => {
          if (currentRequest !== requestNumber || !location.isConnected) return;
          const message = error.code === 1
            ? 'Location permission was denied. Enter the address below.'
            : error.code === 3
              ? 'Finding your location took too long. Enter the address below.'
              : 'Your location is unavailable. Enter the address below.';
          showManual(message);
          status.classList.add('is-error');
        },
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
        );
      } catch {
        showManual('Your location is unavailable. Enter the address below.');
        status.classList.add('is-error');
      }
    });
  }

  let itemAdded = false;
  form.addEventListener('submit', async (event) => {
    for (const location of locations) {
      if (location.querySelector('[data-send-locate]').disabled) {
        event.preventDefault();
        location.querySelector('[data-send-status]').focus();
        return;
      }
      const details = location.querySelector('[data-send-details]');
      details.value = details.value.trim();
      if (!details.value) {
        event.preventDefault();
        details.setCustomValidity('Enter an address or landmark for this point.');
        details.reportValidity();
        details.focus();
        return;
      }
      details.setCustomValidity('');
      if (location.querySelector('[data-send-method]').value === 'Current location' && !location.querySelector('[data-send-map-link]').value) {
        event.preventDefault();
        location.querySelector('[data-send-manual]').click();
        return;
      }
    }

    const api = window.KeeneCartGuard;
    if (!api) return;
    event.preventDefault();
    const submit = form.querySelector('[data-send-submit]');
    if (!submit || submit.disabled) return;
    const error = form.querySelector('[data-send-form-error]');
    error.hidden = true;
    error.textContent = '';
    const noteField = form.querySelector('[data-send-note]');
    const note = noteField?.value.trim() || '';
    if (noteField) noteField.value = note;
    const originalLabel = submit.innerHTML;
    submit.disabled = true;
    submit.textContent = itemAdded ? 'Saving note…' : 'Adding delivery…';
    try {
      if (!itemAdded) {
        const body = new FormData(form);
        if (!note) body.delete('properties[Additional notes]');
        const result = await api.fetch(`${api.root()}cart/add.js`, { method: 'POST', body });
        if (!result.ok) throw new Error('Could not add this delivery to your cart. Please try again.');
        itemAdded = true;
      }
      const noteResponse = await fetch(`${api.root()}cart/update.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ note }),
      });
      const updatedCart = await noteResponse.json();
      if (!noteResponse.ok || String(updatedCart.note || '') !== note) throw new Error('Could not save the order note.');
      window.location.assign(window.FoxTheme?.routes?.cart_url || `${api.root()}cart`);
    } catch {
      error.textContent = itemAdded
        ? 'Your delivery was added to the cart, but the order note could not be saved. Please try again.'
        : 'Could not add this delivery to your cart. Please try again.';
      error.hidden = false;
      error.scrollIntoView({ block: 'center' });
    } finally {
      submit.disabled = false;
      submit.innerHTML = originalLabel;
    }
  });

  form.addEventListener('input', (event) => {
    if (!event.target.matches('[data-send-details]')) return;
    event.target.setCustomValidity('');
  });

  };

  init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
